import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import { uncomma, formatNumberWithCommas, dateFormatDateTimeApi } from "@/utils";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import { fuelRateOptions, washStatusOptions } from "@/constants/selectOptions";
import { useReturnContractMutation } from "@/redux/contractApi";
import dayjs from "dayjs";
import {convertUTCToLocal} from "../../../utils";

export const VehicleReturnPopup = ({ layer = true, onClose, formData, setFormData, resultCallback }) => {
    const { vehicleReturnContractIdx } = formData;
    // 기준 계약 찾기: id가 가장 크거나 createdAt이 가장 최근인 계약
    const contracts = formData.contracts || [];
    let baseContract = contracts[0];
    if (contracts.length > 1) {
        baseContract = contracts.reduce((a, b) => {
            if (a.id && b.id) return a.id > b.id ? a : b;
            if (a.createdAt && b.createdAt) return dayjs(a.createdAt).isAfter(dayjs(b.createdAt)) ? a : b;
            return a;
        });
    }
    // 이전 계약 찾기 (baseContract 기준으로 정렬 후 바로 앞 계약)
    let prevContract = null;
    if (contracts.length > 1 && baseContract) {
        const sorted = [...contracts].sort((a, b) => {
            if (a.id && b.id) return a.id - b.id;
            if (a.createdAt && b.createdAt) return dayjs(a.createdAt).isAfter(dayjs(b.createdAt)) ? 1 : -1;
            return 0;
        });
        const idx = sorted.findIndex((c) => c.id === baseContract.id);
        if (idx > 0) prevContract = sorted[idx - 1];
    }
    // allocation 값 세팅
    const [returnData, setReturnData] = useState(() => {
        const alloc = baseContract.allocation || {};
        console.log(alloc);
        return {
            addressPopupStatus: false,
            releasedAt: alloc.releasedAt || "",
            releaseAddress: alloc.releaseAddress || "",
            releaseAddressDetail: alloc.releaseAddressDetail || "",
            returnedAt: alloc.returnedAt || "",
            returnMileage: alloc.returnMileage || "",
            returnGasAmount: alloc.returnGasAmount || null,
            returnAddress: alloc.returnAddress || "",
            returnAddressDetail: alloc.returnAddressDetail || "",
            washStatus: alloc.washStatus || "FINISH",
        };
    });

    const [addressTarget, setAddressTarget] = useState(""); // "release" or "return"

    useEffect(() => {
        console.log(returnData);
    }, [returnData]);

    const [returnContract, { isLoading: isReturnLoading }] = useReturnContractMutation();

    const returnMethodOptions = [
        { value: "직접진행", label: "직접진행" },
        { value: "탁송진행", label: "탁송진행" },
        { value: "고객내방", label: "고객내방" },
    ];

    useEffect(() => {
        document.body.classList.add("scroll-lock");

        return () => {
            document.body.classList.remove("scroll-lock");
        };
    }, []);

    // 배차일시 disabled 여부
    const releasedAtDisabled = !!(prevContract && prevContract.allocation?.returnedAt);

    // 반납 등록 버튼 이벤트 핸들러
    const handleReturnSubmit = async () => {
        // 필수값 검사
        if (!returnData.releasedAt) {
            setFormData((prev) => ({ ...prev, toastMsg: "배차일시를 입력해주세요." }));
            return;
        }
        if (!returnData.returnedAt) {
            setFormData((prev) => ({ ...prev, toastMsg: "반납일시를 입력해주세요." }));
            return;
        }
        if (dayjs(returnData.returnedAt).isBefore(dayjs(returnData.releasedAt))) {
            setFormData((prev) => ({ ...prev, toastMsg: "반납일시는 배차일시보다 이전일 수 없습니다." }));
            return;
        }
        if (!returnData.washStatus) {
            setReturnData((prev) => ({ ...prev, washStatus: "FINISH" }));
            setFormData((prev) => ({ ...prev, toastMsg: "세차상태를 선택해주세요." }));
            return;
        }
        // API 연동
        const payload = {
            //releasedAt: returnData.releasedAt,
            releasedAt: convertUTCToLocal(new Date(returnData.releasedAt)),
            releaseAddress: returnData.releaseAddress,
            releaseAddressDetail: returnData.releaseAddressDetail,
            //returnedAt: returnData.returnedAt,
            returnedAt: convertUTCToLocal(new Date(returnData.returnedAt)),
            returnMileage: Number(uncomma(returnData.returnMileage)),
            returnGasAmount: returnData.returnGasAmount,
            returnAddress: returnData.returnAddress,
            returnAddressDetail: returnData.returnAddressDetail,
            washStatus: returnData.washStatus,
        };
        try {
            await returnContract({
                contractGroupId: formData.id,
                contractId: baseContract.id,
                body: payload,
            }).unwrap();

            resultCallback(true);
            setFormData((prev) => ({ ...prev, toastMsg: "반납 등록이 완료되었습니다." }));
            onClose();
        } catch (error) {
            resultCallback(false);
            setFormData((prev) => ({ ...prev, toastMsg: error?.message || "반납 등록에 실패하였습니다." }));
        }
    };

    return ReactDOM.createPortal(
        <>
            <div className={layer ? "layer active" : "layer"} onClick={onClose}></div>

            <div className="popup vehicle-return-popup">
                <div className="popup-head">
                    <div className="head-tit">
                        <span className="tit-main">반납 등록</span>
                    </div>
                    <div className="btn-close" onClick={onClose}></div>
                </div>
                <div className="popup-content">
                    <div className="table-container">
                        <div className="table-filter">
                            <div className="filter-row">
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">
                                            배차 일시 <em>*</em>
                                        </span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <DatePicker
                                                locale={ko}
                                                showYearDropdown
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={20}
                                                selected={returnData?.releasedAt}
                                                onChange={(date) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        releasedAt: dateFormatDateTimeApi(date),
                                                    }));
                                                }}
                                                timeInputLabel="시간"
                                                showTimeInput
                                                dateFormat="yyyy.MM.dd HH:mm"
                                                onKeyDown={(e) => e.preventDefault()}
                                                shouldCloseOnSelect={false}
                                                // disabled={releasedAtDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">배차 주소</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="주소"
                                                name="releaseAddress"
                                                value={returnData.releaseAddress || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        releaseAddress: e.target.value,
                                                    }));
                                                }}
                                                onClick={() => {
                                                    setAddressTarget("release");
                                                    setReturnData((prev) => ({ ...prev, addressPopupStatus: true }));
                                                }}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head"></div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="상세주소"
                                                name="releaseAddressDetail"
                                                value={returnData.releaseAddressDetail || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        releaseAddressDetail: e.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">
                                            반납 일시 <em>*</em>
                                        </span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <DatePicker
                                                locale={ko}
                                                showYearDropdown
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={20}
                                                selected={returnData?.returnedAt}
                                                onChange={(date) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnedAt: dateFormatDateTimeApi(date),
                                                    }));
                                                }}
                                                timeInputLabel="시간"
                                                showTimeInput
                                                dateFormat="yyyy.MM.dd HH:mm"
                                                onKeyDown={(e) => e.preventDefault()}
                                                shouldCloseOnSelect={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 방법</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={returnMethodOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="반납방법"
                                                value={returnMethodOptions.find((opt) => opt.value === returnData?.returnMethod)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnMethod: option.value,
                                                    }))
                                                }
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 주소</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="주소"
                                                name="returnAddress"
                                                value={returnData.returnAddress || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnAddress: e.target.value,
                                                    }));
                                                }}
                                                onClick={() => {
                                                    setAddressTarget("return");
                                                    setReturnData((prev) => ({ ...prev, addressPopupStatus: true }));
                                                }}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head"></div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder="상세주소"
                                                name="returnAddressDetail"
                                                value={returnData.returnAddressDetail || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnAddressDetail: e.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 주행거리</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <input
                                                type="text"
                                                placeholder=""
                                                name="returnMileage"
                                                value={returnData.returnMileage || ""}
                                                onChange={(e) => {
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnMileage: e.target.value,
                                                    }));
                                                }}
                                                onInput={formatNumberWithCommas}
                                            />
                                            <span className="input-esset">km</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">반납 유류량</span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={fuelRateOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="유류량 선택"
                                                value={fuelRateOptions.find((opt) => opt.label === returnData?.returnGasAmount)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        returnGasAmount: option.value,
                                                    }))
                                                }
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="filter-item">
                                    <div className="item-head">
                                        <span className="head-tit">
                                            세차 상태 <em>*</em>
                                        </span>
                                    </div>
                                    <div className="item-content">
                                        <div className="item-input">
                                            <Select
                                                options={washStatusOptions}
                                                className="custom-select"
                                                classNamePrefix="custom-select"
                                                isSearchable={false}
                                                placeholder="세차 상태 선택"
                                                value={washStatusOptions.find((opt) => opt.value === returnData?.washStatus)}
                                                onChange={(option) =>
                                                    setReturnData((prev) => ({
                                                        ...prev,
                                                        washStatus: option.value,
                                                    }))
                                                }
                                                menuPortalTarget={document.body}
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="popup-bottom">
                    <button className="btn btn-primary btn-cancel" onClick={onClose}>
                        취소
                    </button>
                    <button className="btn btn-primary btn-confirm" onClick={handleReturnSubmit}>
                        반납 등록
                    </button>
                </div>
            </div>

            {/* 주소 검색 팝업 */}
            {returnData.addressPopupStatus && (
                <SearchAddress
                    modalStatus={returnData.addressPopupStatus}
                    setModalStatus={() =>
                        setReturnData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) => {
                        if (addressTarget === "release") {
                            setReturnData((prev) => ({
                                ...prev,
                                releaseAddress: data.address,
                                releaseBCode: data.bcode,
                            }));
                        } else if (addressTarget === "return") {
                            setReturnData((prev) => ({
                                ...prev,
                                returnAddress: data.address,
                                returnBCode: data.bcode,
                            }));
                        }
                        setAddressTarget("");
                    }}
                    layerType="active_transparent"
                />
            )}
        </>,
        document.body
    );
};
